<template>
  <v-card flat border class="rr-activity pa-4">
    <div class="rr-activity__toolbar d-flex flex-wrap align-center ga-2 mb-3">
      <div class="rr-segmented">
        <button
          type="button"
          class="rr-segmented__btn"
          :class="{ 'rr-segmented__btn--active': metricType === 'orders' }"
          @click="selectMetric('orders')"
        >
          {{ $t("moduli_rr.overview_stats.metric_orders") }}
        </button>
        <button
          type="button"
          class="rr-segmented__btn"
          :class="{ 'rr-segmented__btn--active': metricType === 'sales' }"
          @click="selectMetric('sales')"
        >
          {{ $t("moduli_rr.overview_stats.metric_sales") }}
        </button>
      </div>

      <v-autocomplete
        v-model="institution"
        :items="institutionItems"
        item-title="title"
        item-value="value"
        variant="outlined"
        density="compact"
        hide-details
        clearable
        auto-select-first
        class="rr-activity__select"
        @update:model-value="onInstitutionChange"
      />

      <v-select
        v-model="timePeriod"
        :items="timePeriodItems"
        item-title="title"
        item-value="value"
        variant="outlined"
        density="compact"
        hide-details
        class="rr-activity__select rr-activity__select--period"
        @update:model-value="onPeriodChange"
      />

      <div class="rr-date-nav d-flex align-center">
        <v-btn
          icon
          variant="text"
          size="small"
          :disabled="loading"
          @click="shiftPeriod(-1)"
        >
          <v-icon size="20">mdi-chevron-left</v-icon>
        </v-btn>
        <span class="rr-date-nav__label text-body-2 font-weight-medium px-1">
          {{ periodLabel }}
        </span>
        <v-btn
          icon
          variant="text"
          size="small"
          :disabled="loading || !canGoNext"
          @click="shiftPeriod(1)"
        >
          <v-icon size="20">mdi-chevron-right</v-icon>
        </v-btn>
      </div>

      <v-spacer class="d-none d-sm-flex" />

      <div class="rr-chart-type d-flex align-center">
        <v-btn
          icon
          variant="text"
          size="small"
          :color="chartType === 'bar' ? 'primary' : undefined"
          @click="setChartType('bar')"
        >
          <v-icon size="20">mdi-chart-bar</v-icon>
        </v-btn>
        <v-btn
          icon
          variant="text"
          size="small"
          :color="chartType === 'line' ? 'primary' : undefined"
          @click="setChartType('line')"
        >
          <v-icon size="20">mdi-chart-line</v-icon>
        </v-btn>
      </div>
    </div>

    <div
      v-if="!loading && buckets.length"
      class="rr-activity__summary d-flex flex-wrap align-baseline ga-4 mb-3"
    >
      <div class="rr-summary-item">
        <span class="rr-summary-item__label">
          {{ $t("moduli_rr.overview_stats.summary_total") }}
        </span>
        <span class="rr-summary-item__value">{{ formatNumber(summary.total) }}</span>
      </div>
      <div class="rr-summary-item">
        <span class="rr-summary-item__label">{{ averageLabel }}</span>
        <span class="rr-summary-item__value">{{ formatDecimal(summary.average) }}</span>
      </div>
      <div class="rr-summary-item">
        <span class="rr-summary-item__label">
          {{ $t("moduli_rr.overview_stats.summary_peak") }}
        </span>
        <span class="rr-summary-item__value">
          {{ formatNumber(summary.peak) }}
          <span
            v-if="summary.peakLabels"
            class="rr-summary-item__muted"
          >
            ({{ summary.peakLabels }})
          </span>
        </span>
      </div>
      <div
        v-if="summary.deltaPct !== null"
        class="rr-summary-item"
        :class="summary.deltaPct >= 0 ? 'rr-summary-item--up' : 'rr-summary-item--down'"
      >
        <span class="rr-summary-item__label">{{ vsPreviousLabel }}</span>
        <span class="rr-summary-item__value">
          {{ summary.deltaPct >= 0 ? "+" : "" }}{{ formatDecimal(summary.deltaPct) }}%
        </span>
      </div>
    </div>

    <v-progress-linear
      v-if="loading"
      indeterminate
      color="#185FA5"
      class="mb-2"
    />

    <chart
      v-if="!loading && chartData.labels.length"
      :key="chartKey"
      :type="chartType"
      :data="chartData"
      :height="280"
      :extra-options="chartOptions"
      :plugins="chartPlugins"
    />
    <v-alert
      v-else-if="!loading"
      type="info"
      variant="tonal"
      density="compact"
    >
      {{ $t("moduli_rr.overview_stats.no_chart_data") }}
    </v-alert>
  </v-card>
</template>

<script>
import moment from "moment";
import getRequest from "@/api/getRequest";
import Chart from "@/components/chart.vue";

const CHART_BAR = "#85B7EB";
const CHART_PEAK = "#185FA5";

/** Always group thousands with `.` (it-IT skips grouping under 10.000). */
function formatItInt(value) {
  const n = Math.round(Number(value) || 0);
  const sign = n < 0 ? "-" : "";
  return sign + String(Math.abs(n)).replace(/\B(?=(\d{3})+(?!\d))/g, ".");
}

export default {
  name: "ModuliRrActivityChart",
  components: { chart: Chart },
  props: {
    institutions: { type: Array, default: () => [] },
  },
  data: () => ({
    loading: false,
    metricType: "orders",
    chartType: "bar",
    timePeriod: "Month",
    institution: "all",
    time: new Date().toISOString().substr(0, 7),
    buckets: [],
    previousTotal: 0,
    total: 0,
  }),
  computed: {
    activeTitle() {
      return this.metricType === "sales"
        ? this.$t("moduli_rr.overview_stats.rr_sales")
        : this.$t("moduli_rr.overview_stats.rr_orders");
    },
    institutionItems() {
      return [
        {
          title: this.$t("moduli_rr.overview_stats.all_institutions"),
          value: "all",
        },
        ...(this.institutions || []).map((i) => ({
          title: i.title,
          value: String(i._id),
        })),
      ];
    },
    timePeriodItems() {
      return [
        {
          title: this.$t("home.charts.time_period_items.month"),
          value: "Month",
        },
        {
          title: this.$t("home.charts.time_period_items.week"),
          value: "Week",
        },
        {
          title: this.$t("home.charts.time_period_items.day"),
          value: "Day",
        },
      ];
    },
    periodMoment() {
      if (this.timePeriod === "Month") {
        return moment(`${this.time}-01`, "YYYY-MM-DD");
      }
      return moment(this.time, "YYYY-MM-DD");
    },
    periodLabel() {
      const m = this.periodMoment;
      if (!m.isValid()) return this.time;
      if (this.timePeriod === "Month") {
        return m.format("MMMM YYYY");
      }
      if (this.timePeriod === "Week") {
        const start = m.clone().startOf("isoWeek");
        const end = m.clone().endOf("isoWeek");
        return `${start.format("D MMM")} – ${end.format("D MMM YYYY")}`;
      }
      return m.format("D MMMM YYYY");
    },
    canGoNext() {
      const now = moment();
      if (this.timePeriod === "Month") {
        return this.periodMoment.isBefore(now, "month");
      }
      return this.periodMoment.isBefore(now, "day");
    },
    averageLabel() {
      if (this.timePeriod === "Day") {
        return this.$t("moduli_rr.overview_stats.summary_avg_hour");
      }
      if (this.timePeriod === "Week") {
        return this.$t("moduli_rr.overview_stats.summary_avg_day");
      }
      return this.$t("moduli_rr.overview_stats.summary_avg_day");
    },
    vsPreviousLabel() {
      if (this.timePeriod === "Month") {
        return this.$t("moduli_rr.overview_stats.summary_vs_month", {
          month: this.periodMoment.clone().subtract(1, "month").format("MMMM"),
        });
      }
      if (this.timePeriod === "Week") {
        return this.$t("moduli_rr.overview_stats.summary_vs_week");
      }
      return this.$t("moduli_rr.overview_stats.summary_vs_day");
    },
    values() {
      return (this.buckets || []).map((b) => b.value || 0);
    },
    peakValue() {
      if (!this.values.length) return 0;
      return Math.max(...this.values);
    },
    summary() {
      const values = this.values;
      const total = this.total || values.reduce((s, v) => s + v, 0);
      const denom = values.length || 1;
      const average = total / denom;
      const peak = this.peakValue;
      const peakBuckets = (this.buckets || []).filter(
        (b) => (b.value || 0) === peak && peak > 0,
      );
      const peakLabels = peakBuckets
        .map((b) => this.formatPeakLabel(b))
        .filter(Boolean)
        .join(", ");

      let deltaPct = null;
      if (this.previousTotal > 0) {
        deltaPct = ((total - this.previousTotal) / this.previousTotal) * 100;
      } else if (total > 0 && this.previousTotal === 0) {
        deltaPct = 100;
      } else if (total === 0 && this.previousTotal === 0) {
        deltaPct = 0;
      }

      return {
        total,
        average,
        peak,
        peakLabels,
        deltaPct,
      };
    },
    chartLabels() {
      return (this.buckets || []).map((element) => {
        if (this.timePeriod === "Day") {
          return moment(element.startingTime).format("H");
        }
        if (this.timePeriod === "Week") {
          return moment(element.startingTime).format("ddd D");
        }
        return moment(element.startingTime).format("D");
      });
    },
    barColors() {
      const peak = this.peakValue;
      return this.values.map((v) =>
        peak > 0 && v === peak ? CHART_PEAK : CHART_BAR,
      );
    },
    chartData() {
      const values = this.values;
      if (this.chartType === "line") {
        return {
          labels: this.chartLabels,
          datasets: [
            {
              label: this.activeTitle,
              data: values,
              backgroundColor: "rgba(24, 95, 165, 0.12)",
              borderColor: CHART_PEAK,
              borderWidth: 2,
              fill: true,
              pointBackgroundColor: this.barColors,
              pointBorderColor: this.barColors,
              pointRadius: 3,
            },
          ],
        };
      }
      return {
        labels: this.chartLabels,
        datasets: [
          {
            label: this.activeTitle,
            data: values,
            backgroundColor: this.barColors,
            borderWidth: 0,
          },
        ],
      };
    },
    chartKey() {
      return `${this.metricType}-${this.timePeriod}-${this.time}-${this.institution}-${this.chartType}-${this.buckets.length}-${this.peakValue}`;
    },
    chartOptions() {
      return {
        legend: { display: false },
        layout: {
          padding: { top: 18 },
        },
        tooltips: {
          mode: "index",
          intersect: false,
        },
        scales: {
          xAxes: [
            {
              ticks: {
                autoSkip: false,
                maxRotation: 0,
                minRotation: 0,
                fontSize: 11,
              },
              gridLines: { display: false },
              categoryPercentage: 0.85,
              barPercentage: 0.9,
            },
          ],
          yAxes: [
            {
              ticks: { beginAtZero: true, precision: 0 },
              gridLines: { color: "rgba(0,0,0,0.06)" },
            },
          ],
        },
      };
    },
    chartPlugins() {
      if (this.chartType !== "bar") return [];
      return [
        {
          afterDatasetsDraw(chart) {
            const { ctx } = chart;
            const meta = chart.getDatasetMeta(0);
            if (!meta || !meta.data) return;
            ctx.save();
            ctx.fillStyle = "rgba(0, 0, 0, 0.7)";
            ctx.font = "600 10px sans-serif";
            ctx.textAlign = "center";
            ctx.textBaseline = "bottom";
            meta.data.forEach((bar, index) => {
              const value = chart.data.datasets[0].data[index];
              if (value == null || value === 0) return;
              const model = bar._model || bar;
              const x = model.x != null ? model.x : bar.tooltipPosition().x;
              const y =
                (model.y != null ? model.y : bar.tooltipPosition().y) - 4;
              ctx.fillText(formatItInt(value), x, y);
            });
            ctx.restore();
          },
        },
      ];
    },
  },
  watch: {
    institutions: {
      handler() {
        this.loadChart();
      },
      deep: true,
    },
  },
  mounted() {
    this.loadChart();
  },
  methods: {
    selectMetric(type) {
      if (this.metricType === type) return;
      this.metricType = type;
      this.loadChart();
    },
    onInstitutionChange(value) {
      this.institution = value || "all";
      this.loadChart();
    },
    formatNumber(n) {
      return formatItInt(n);
    },
    formatDecimal(n) {
      const value = Number(n) || 0;
      const [intPart, fracPart] = value.toFixed(1).split(".");
      const grouped = formatItInt(intPart);
      if (!fracPart || fracPart === "0") return grouped;
      return `${grouped},${fracPart}`;
    },
    formatPeakLabel(bucket) {
      const m = moment(bucket.startingTime);
      if (!m.isValid()) return "";
      if (this.timePeriod === "Day") {
        return m.format("HH:mm");
      }
      if (this.timePeriod === "Week") {
        return m.format("D MMM");
      }
      return m.format("D MMM");
    },
    onPeriodChange() {
      if (this.timePeriod === "Month") {
        const now = new Date();
        this.time = `${now.getFullYear()}-${`${now.getMonth() + 1}`.padStart(2, "0")}`;
      } else {
        this.time = moment().format("YYYY-MM-DD");
      }
      this.loadChart();
    },
    shiftPeriod(direction) {
      const m = this.periodMoment.clone();
      if (this.timePeriod === "Month") {
        m.add(direction, "month");
        this.time = m.format("YYYY-MM");
      } else if (this.timePeriod === "Week") {
        m.add(direction, "week");
        this.time = m.format("YYYY-MM-DD");
      } else {
        m.add(direction, "day");
        this.time = m.format("YYYY-MM-DD");
      }
      this.loadChart();
    },
    setChartType(type) {
      this.chartType = type;
    },
    loadChart() {
      this.loading = true;
      const institutionParam =
        this.institution && this.institution !== "all"
          ? `&institution=${this.institution}`
          : "&institution=all";
      getRequest(
        "/moduli-rr/overview/timeseries",
        `institution_id=${this.$store.getters.institutionId}&role=${
          this.$store.getters.role
        }&type=${this.metricType}&period=${this.timePeriod}&time=${
          this.time
        }${institutionParam}`,
      )
        .then((response) => {
          this.buckets = response.buckets || [];
          this.previousTotal = response.previous_total || 0;
          this.total =
            typeof response.total === "number"
              ? response.total
              : (response.buckets || []).reduce(
                  (s, b) => s + (b.value || 0),
                  0,
                );
        })
        .catch(() => {
          this.buckets = [];
          this.previousTotal = 0;
          this.total = 0;
        })
        .finally(() => {
          this.loading = false;
        });
    },
  },
};
</script>

<style scoped>
.rr-activity__toolbar {
  row-gap: 8px;
}

.rr-activity__select {
  max-width: 280px;
  min-width: 220px;
}

.rr-activity__select--period {
  max-width: 160px;
  min-width: 140px;
}

.rr-segmented {
  display: inline-flex;
  border: 1px solid rgba(0, 0, 0, 0.12);
  border-radius: 8px;
  overflow: hidden;
  background: #fff;
}

.rr-segmented__btn {
  border: 0;
  background: transparent;
  padding: 6px 14px;
  font-size: 0.875rem;
  font-weight: 500;
  color: rgba(0, 0, 0, 0.7);
  cursor: pointer;
  line-height: 1.4;
}

.rr-segmented__btn + .rr-segmented__btn {
  border-left: 1px solid rgba(0, 0, 0, 0.12);
}

.rr-segmented__btn--active {
  background: #212121;
  color: #fff;
}

.rr-date-nav__label {
  min-width: 110px;
  text-align: center;
  text-transform: capitalize;
  white-space: nowrap;
}

.rr-summary-item {
  display: inline-flex;
  align-items: baseline;
  gap: 6px;
}

.rr-summary-item__label {
  color: rgba(0, 0, 0, 0.55);
  font-size: 0.875rem;
}

.rr-summary-item__value {
  font-weight: 600;
  font-size: 0.95rem;
  color: rgba(0, 0, 0, 0.87);
}

.rr-summary-item__muted {
  font-weight: 400;
  color: rgba(0, 0, 0, 0.55);
  font-size: 0.85rem;
}

.rr-summary-item--up .rr-summary-item__value {
  color: #2e7d32;
}

.rr-summary-item--down .rr-summary-item__value {
  color: #c62828;
}

@media (max-width: 600px) {
  .rr-activity__select {
    max-width: 100%;
    flex: 1 1 140px;
  }
}
</style>
